import type { VnnicFileFormat } from '../../lib/uploadRule';
/**
 * "Hồ sơ tên miền" — the dossier queue (subsystems/identity/facade/adapters/admin-api/src/admin/dossier_queue.rs,
 * crates/compliance/src/dossier_queue.rs).
 *
 * One row per domain whose activation waits for a dossier (registration, transfer-in,
 * registrant change, name change), plus recently completed ones. The stage is derived on the
 * server from the dossier, the registrant's verifications, the declaration uploads, the open
 * work items and the activation workflow. Decisions go through the work items (resolve /
 * assign); "Nhắc khách" e-mails the registrant (once per 24 h per dossier).
 */

import type { CallOptions, DossierVnnicFiles, ISODateTime, MissingItem, RecordedReason, VnnicSyncErrorCode, VnnicSyncStatus, WriteOptions } from '../types';

export const DOSSIER_STAGES = ['AWAITING_CUSTOMER', 'AUTO_CHECKING', 'NEEDS_REVIEW', 'WARNING', 'ACTIVATING', 'ACTIVATED'] as const;
export type DossierStage = (typeof DOSSIER_STAGES)[number];

/** How the registrant is being (or was) verified. */
export const DOSSIER_METHODS = ['EKYC', 'VNEID', 'DECLARATION_SIGNED', 'DECLARATION_SCAN', 'DECLARATION', 'DOCUMENTS', 'STAFF'] as const;
export type DossierMethod = (typeof DOSSIER_METHODS)[number];

/** What the system found. */
export type DossierFinding =
  | 'NOTHING_SUBMITTED'
  | 'CORRECTION_REQUESTED'
  | 'VERIFICATION_FAILED'
  | 'CHECKING'
  | 'NEEDS_STAFF'
  | 'WARNING'
  | 'AUTO_APPROVED'
  | 'STAFF_APPROVED';

export type ActivationWorkflow = 'DOMAIN_REGISTRATION' | 'DOMAIN_TRANSFER_IN' | 'DOMAIN_REGISTRANT_CHANGE' | 'DOMAIN_NAME_CHANGE';

export interface DossierQueueRow {
  dossier_id: string;
  dossier_status: string;
  dossier_version: number;
  domain_id: string;
  domain_name: string;
  unicode_name: string;
  tld: string;
  lifecycle_state: string;
  operation: ActivationWorkflow | null;
  workflow_id: string | null;
  workflow_state: string | null;
  workflow_step: string | null;
  order_item_id: string | null;
  order_id: string | null;
  customer_id: string;
  customer_name: string | null;
  subject_id: string | null;
  subject_version_id: string | null;
  /** The registrant's legal name (never an identity number); null for callers without `subject.read`. */
  registrant_name: string | null;
  registrant_type: string | null;
  stage: DossierStage;
  method: DossierMethod | null;
  finding: DossierFinding;
  /** The work item kind (or WORKFLOW_FAILED / WORKFLOW_MANUAL_REVIEW) behind NEEDS_STAFF / WARNING. */
  finding_kind: string | null;
  /** Non-sensitive facts of that item: reason codes, field comparison, failed checks, changed parts. */
  finding_detail: {
    reason_codes?: string[];
    comparison?: Partial<Record<'name' | 'document_number' | 'birth', string>>;
    upload_kind?: 'SIGNED_PDF' | 'SCAN';
    everything_matches?: boolean;
    failed_checks?: string[];
    changed?: string[];
    sync_status?: VnnicSyncStatus;
    last_error_code?: VnnicSyncErrorCode;
  };
  review_item_id: string | null;
  warning_item_id: string | null;
  waiting_since: ISODateTime | null;
  /** Registrations and transfers-in: 7 days from the start. */
  deadline: ISODateTime | null;
  /** A review item is open: the deadline does not cancel the order while staff decide. */
  deadline_paused: boolean;
  due_soon: boolean;
  overdue: boolean;
  assignee_id: string | null;
  assignee_name: string | null;
  last_reminder_at: ISODateTime | null;
  reminders_sent: number;
  completed_at: ISODateTime | null;
}

export interface DossierCounts {
  awaiting_customer: number;
  auto_checking: number;
  needs_review: number;
  warning: number;
  due_soon: number;
  activating: number;
  activated_today: number;
  activated_recent: number;
}

export interface DossierQueueQuery {
  /** Default: every stage but ACTIVATED. */
  stage?: DossierStage;
  method?: DossierMethod;
  tld?: string;
  /** `me`, `unassigned` or a staff id. */
  assignee?: string;
  due_soon?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface DossierQueuePage {
  items: DossierQueueRow[];
  total: number;
  counts: DossierCounts;
}

export interface DossierCheck {
  code: string;
  status: 'PASS' | 'FAIL' | 'UNKNOWN';
  reason?: string | null;
  message?: string | null;
}

/** Everything the review panel shows (identifiers masked). */
export interface DossierQueueDetail extends DossierQueueRow {
  registrant: {
    subject_id: string;
    subject_version_id: string;
    version_number: number;
    /** Null for callers without `subject.read` (like `registrant_name`). */
    legal_name: string | null;
    subject_type: string;
    country_code: string;
    current_version_id: string | null;
    identifiers: { type: string; country: string; hint: string }[];
    /** Null for callers without `subject.read`. */
    emails: string[] | null;
  } | null;
  verifications: {
    id: string;
    method: string;
    status: string;
    provider: string | null;
    created_at: ISODateTime;
    verified_at: ISODateTime | null;
    reviewer_id: string | null;
    reviewer_name: string | null;
    pinned: boolean | null;
  }[];
  declarations: {
    declaration_id: string;
    submission_id: string;
    template: string;
    upload_kind: 'SIGNED_PDF' | 'SCAN' | null;
    status: string;
    checks: DossierCheck[];
    uploaded_at: ISODateTime;
    decided_at: ISODateTime | null;
    work_item_id: string | null;
  }[];
  documents: { document_version_id: string; document_id: string; purpose: string; bound_at: ISODateTime }[];
  history: { from: string | null; to: string; reason: RecordedReason | null; actor_type: string; at: ISODateTime; missing: MissingItem[] }[];
  work_items: {
    id: string;
    kind: string;
    status: string;
    severity: string;
    summary: string;
    opened_at: ISODateTime;
    resolved_at: ISODateTime | null;
    resolution: string | null;
    resolution_reason: RecordedReason | null;
    assigned_to: string | null;
  }[];
  reminders: { id: string; sent_at: ISODateTime; recipient_kind: 'REGISTRANT' | 'CUSTOMER'; sent_by: string; sent_by_name: string | null; note: string | null }[];
  notes: { id: string; body: string; created_at: ISODateTime; created_by: string; created_by_name: string | null }[];
  next_reminder_at: ISODateTime | null;
  /** `.vn`: the files VNNIC's HSTM receives (VNNIC HSTM-API v1.3 Appendix 2) and what is missing. */
  hstm?: DossierVnnicFiles | null;
}

/** A file for one of the dossier's HSTM slots (e.g. the sensitive-keyword commitment). */
export interface DossierFileUpload {
  /** The catalogue document type ("Loại hồ sơ", `DossierDocumentType.id`). */
  document_type?: string;
  /** Older callers: the type's binding purpose. */
  purpose?: string;
  mime_type: string;
  file_name?: string;
  /** Base64. */
  data: string;
  reason: string;
}

export interface DossierReminder {
  reminder_id: string;
  notification_id: string | null;
  recipient_kind: 'REGISTRANT' | 'CUSTOMER';
  sent_at: ISODateTime;
}

/** The registrants a document type applies to ("Áp dụng cho chủ thể"): every non-individual legal form is ORGANIZATION. */
export const DOCUMENT_SUBJECT_SCOPES = ['ALL', 'INDIVIDUAL', 'ORGANIZATION'] as const;
export type DocumentSubjectScope = (typeof DOCUMENT_SUBJECT_SCOPES)[number];

/** A document type required in a dossier ("bắt buộc") and how it is satisfied. */
export interface DossierRequiredType {
  id: string;
  code: number;
  name_vi: string;
  name_en: string;
  /** FILE: a file of the type is bound; EKYC: filled by the passed eKYC; null: missing. */
  satisfied_by: 'FILE' | 'EKYC' | null;
}

/** HSTM dossier operations (Phụ lục 1 Bảng 2), the keys of `required_operations`. */
export const DOSSIER_OPERATIONS = ['REGISTER', 'CHANGE_INFORMATION', 'CHANGE_REGISTRANT_NAME', 'RETURN', 'TRANSFER_RIGHT_OF_USE', 'CHANGE_REGISTRAR'] as const;
export type DossierOperation = (typeof DOSSIER_OPERATIONS)[number];

/**
 * One dossier document type ("Loại hồ sơ", `dossier.document_types`): the single list of what a
 * domain's dossier may hold. `code` is the HostBill number staff know.
 */
export interface DossierDocumentType {
  id: string;
  code: number;
  name_vi: string;
  name_en: string;
  /** "Download": a template can be downloaded (`template_codes`, the declaration forms). */
  downloadable_template: boolean;
  /**
   * "Bắt buộc" for these operations — exactly within the type's scope: its TLDs (`tld_zones`)
   * and registrants (`subject_scope`). The single source of the required files, every TLD.
   */
  required_operations: DossierOperation[];
  /** "eKYC": filled by the eKYC flow. */
  filled_by_ekyc: boolean;
  /** "Only download": the customer downloads the template; staff upload. */
  customer_download_only: boolean;
  /** "Only admin": staff only. */
  staff_only: boolean;
  allowed_extensions: string[];
  max_bytes: number;
  /** Upload types only: VNNIC's file rule for this type (HSTM Phụ lục 1 Bảng 3); null when it is not sent to VNNIC. */
  vnnic_format?: VnnicFileFormat | null;
  template_codes: string[];
  /** "Áp dụng cho đuôi": null every TLD ("Mọi đuôi"), else the zones. */
  tld_zones: string[] | null;
  /** "Áp dụng cho chủ thể". */
  subject_scope: DocumentSubjectScope;
  /** VNNIC HSTM file type (1–16) and document type (1–6). */
  hstm_file_type: number | null;
  hstm_document_type: number | null;
  /** HSTM verification methods (1–5) it applies to; empty: all. */
  verification_methods: number[];
  purposes: string[];
  /** null: uploaded by another flow (the declaration). */
  upload_purpose: string | null;
  /** AUTO: the registrar signs it automatically (one type: the domain declaration). */
  registrar_signature: 'AUTO' | 'OPTIONAL';
  auto_sign_kind: string | null;
  auto_sign_purposes: string[];
  every_dossier: boolean;
  active: boolean;
  sort_order: number;
  source: string;
  version: number;
  updated_at: ISODateTime;
  updated_by: string | null;
}

/** `PUT /dossier-document-types/{id}`: what staff edit (reason recorded, `version` checked). */
export interface DossierDocumentTypeEdit {
  version: number;
  reason: string;
  name_vi: string;
  name_en: string;
  downloadable_template: boolean;
  required_operations: DossierOperation[];
  filled_by_ekyc: boolean;
  customer_download_only: boolean;
  staff_only: boolean;
  allowed_extensions: string[];
  max_bytes: number;
  template_codes: string[];
  tld_zones: string[] | null;
  subject_scope: DocumentSubjectScope;
  hstm_file_type: number | null;
  hstm_document_type: number | null;
  verification_methods: number[];
  active: boolean;
}

export interface DossierQueueApi {
  listDossierQueue(query: DossierQueueQuery, opts?: CallOptions): Promise<DossierQueuePage>;
  dossierQueueSummary(opts?: CallOptions): Promise<DossierCounts>;
  getDossierQueueItem(dossierId: string, opts?: CallOptions): Promise<DossierQueueDetail>;
  remindDossier(dossierId: string, body: { note?: string }, opts?: WriteOptions): Promise<DossierReminder>;
  /** An internal staff note ("Ghi chú"), audited. */
  addDossierNote(dossierId: string, body: { body: string }, opts?: WriteOptions): Promise<{ id: string; dossier_id: string; created_at: ISODateTime }>;
  /**
   * Uploads a file of any document type to the dossier (nothing restricts staff); a PDF's digital
   * signature is checked by identity-worker. `pipeline`: DECLARATION — #14 of an organisation
   * went through the declaration pipeline (a submission: checks, staff decision, then the
   * registrar's signature); DOSSIER_FILE — stored as a file of the type.
   */
  uploadDossierFile(
    dossierId: string,
    body: DossierFileUpload,
    opts?: WriteOptions,
  ): Promise<{
    dossier_id: string;
    document_version_id: string;
    document_type?: string;
    purpose: string;
    file_name?: string | null;
    hstm_file_type: number | null;
    signature_check: 'PENDING' | 'NOT_APPLICABLE';
    pipeline?: 'DECLARATION' | 'DOSSIER_FILE';
    declaration_id?: string;
    submission_id?: string;
  }>;
  /** The document types staff may upload to this dossier (its TLD and operation), and the types required in it ("bắt buộc"). */
  listDossierUploadTypes(dossierId: string, opts?: CallOptions): Promise<{ items: DossierDocumentType[]; required?: DossierRequiredType[] }>;
  /** "Loại hồ sơ": the catalogue. */
  listDossierDocumentTypes(opts?: CallOptions): Promise<{ items: DossierDocumentType[] }>;
  /** Edits one type (`compliance.override`; reason; 409 VERSION_CONFLICT when it changed). */
  updateDossierDocumentType(id: string, body: DossierDocumentTypeEdit, opts?: WriteOptions): Promise<DossierDocumentType>;
  /** Marks the domain name as containing a sensitive keyword (HSTM then requires the commitment, file 7). */
  setDossierCase(dossierId: string, body: { sensitive_keyword: boolean; reason: string }, opts?: WriteOptions): Promise<{ dossier_id: string; sensitive_keyword: boolean; hstm: DossierVnnicFiles | null }>;
}
